import type { FormId } from "@@/shared/form/form-and-field-id";
import { type DeepPathValueType } from "@towni/common";
import { useMemo } from "react";
import { type ZodSchema } from "zod";
import type { Form3ErrorMessage } from "./form3.error-message";
import type { TForm3 } from "./form3.form";
import { useForm3 } from "./form3.use-form";
import { getFormValueAtPath } from "./get-value-at-path";

type FormField<
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
    FieldId extends string,
    DesiredValueType,
> = {
    initialValue: DesiredValueType | undefined | null;
    value: DesiredValueType | undefined | null;
    fieldId: FieldId;
    touched: boolean;
    dirty: boolean;
    isChanged: boolean;
    isRequired: boolean;
    form: TForm3<FormSchema, FormData, FieldId>;
    setValue: <FieldValue>(
        value:
            | FieldValue
            | ((
                  currentValue: FieldValue | null | undefined,
              ) => FieldValue | null | undefined),
    ) => void;
    setTouched: (touched: boolean) => void;
    setAllToTouched: () => void;
    setAllToDirty: () => void;
    setDirty: (dirty: boolean) => void;
    getSchema: () => ZodSchema<DesiredValueType> | undefined;
    validate: (options?: {
        preProcessValue?: (
            value: DesiredValueType | undefined | null,
        ) => DesiredValueType | undefined;
        customErrorMessage?: Form3ErrorMessage;
    }) => Form3ErrorMessage[];
};

/**
 * Custom hook to read the state and manage the behavior of a single form field.
 */
const useFormField = <
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
    FieldId extends string,
    DesiredValueType,
>(params: {
    readonly formId?: FormId | undefined;
    readonly fieldId: FieldId | undefined;
}): FormField<FormSchema, FormData, FieldId, DesiredValueType> => {
    const formId = params.formId;
    const fieldId = params.fieldId;
    if (!fieldId) throw new Error("Field id not provided");
    const [form] = useForm3<FormSchema, FormData, FieldId>({ formId });
    if (!form) throw new Error(`Form with id ${formId} not found`);
    // type FieldValue =
    //     DesiredValueType extends DeepPathValueType<FormData, FieldId>
    //         ? DeepPathValueType<FormData, FieldId>
    //         : never;
    return useMemo(
        (): FormField<FormSchema, FormData, FieldId, DesiredValueType> => ({
            value: form.getFieldValue(fieldId) as DesiredValueType | undefined,
            initialValue: getFormValueAtPath(form.initialData, fieldId),
            touched: form.touched[fieldId] || false,
            dirty: form.dirty[fieldId] || false,
            isChanged: form.isFieldChanged(fieldId),
            fieldId,
            isRequired: !!form.getFieldSchema(fieldId)?.isOptional === false,
            setValue: value => {
                return form.setFormField(fieldId, value);
            },
            getSchema: () =>
                form.getFieldSchema(fieldId) as ZodSchema<DesiredValueType>,
            setTouched: (touched: boolean) => {
                form.setTouched(fieldId, touched);
            },
            form,
            setAllToTouched: () => {
                throw new Error("Not implemented");
                // NOT IMPLEMENTED
                // we probably want to keep track of fields used in the form
                // and use them fields, not set all possible combinations
            },
            setAllToDirty: () => {
                throw new Error("Not implemented");
                // NOT IMPLEMENTED
                // we probably want to keep track of fields used in the form
                // and use them fields, not set all possible combinations
            },
            setDirty: (dirty: boolean) => {
                form.setDirty(fieldId, dirty);
            },
            validate: options => {
                return form.validateField(fieldId, {
                    ...options,
                    preProcessValue: options?.preProcessValue as (
                        value:
                            | DeepPathValueType<FormData, FieldId>
                            | undefined
                            | null,
                    ) => DeepPathValueType<FormData, FieldId> | undefined,
                });
            },
        }),
        [form, fieldId],
    );
};

const useFormFieldActions = <
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
    FieldId extends string,
    DesiredValueType,
>(params: {
    formId: FormId | undefined;
    fieldId: FieldId;
}) => {
    const field = useFormField<FormSchema, FormData, FieldId, DesiredValueType>(
        {
            formId: params.formId,
            fieldId: params.fieldId,
        },
    );

    return useMemo(
        () => ({
            setValue: field.setValue,
            setTouched: field.setTouched,
            setDirty: field.setDirty,
        }),
        [field.setDirty, field.setTouched, field.setValue],
    );
};

export {
    useFormField as useForm3Field,
    useFormFieldActions as useForm3FieldActions,
};
export type { FormField as Form3Field };
